/**
 * Platform-wide reservation policy. Controlled by the operating company (YOKOSO PAY)
 * rather than individual store owners to keep customer expectations consistent.
 *
 * To change globally: edit these constants. Future: migrate to a Firestore
 * `config/reservationPolicy` document so operations staff can tune without a deploy.
 */
// #1357 何日先まで予約を受け付けるかの**既定値**。店舗が
// `reservationConfig.bookableDaysAhead` を設定していればそちらが優先される。
// サーバ側の `services/reservation_policy.py` と同じ値を保つこと。
export const DEFAULT_BOOKABLE_DAYS_AHEAD = 30

/**
 * 予約を受け付ける最短リードタイム (分)。「今から何分後以降なら予約できるか」。
 *
 * これが無かったため、当日の**過ぎた時刻**がそのまま選べていた (14:00 に 08:00 の
 * 枠が出て、直接 POST すれば通った)。日付単位の締切 (bookableDaysAhead /
 * reservationDeadlineDays) は「何日前まで」の話で当日の時刻を見ていない。
 *
 * サーバ側 (services/reservation_policy.py) の DEFAULT_MIN_LEAD_MINUTES と同値。
 */
export const DEFAULT_MIN_LEAD_MINUTES = 60

export const RESPONSE_DEADLINE_HOURS = 48
export const CANCEL_DEADLINE_HOURS = 24

// #1371 キャンセル手続き料 (%)。返金時に**加盟店が受け取る**割合。
// サーバ側の `services/reservation_policy.py` と同じ値を保つこと。
//
// カード決済は成立時点で手数料が発生し、**返金しても返還されない**。全額返金だと
// 仕込みも発生していない 7 日以上前のキャンセルでも加盟店が持ち出しになる。
// 6% は海外カード 5.1% を上回る水準で、通貨変換や料率改定の変動も吸収する。
export const CANCELLATION_HANDLING_FEE_PCT = 6

/** 前払いコースの段階返金率。refund-policy.md §9.1 (a) と整合。 */
export const PREPAID_REFUND_TIERS: { labelKey: string; pct: number }[] = [
  { labelKey: 'cancellation.tier7DaysPlus', pct: 100 },
  { labelKey: 'cancellation.tier3To7Days', pct: 70 },
  { labelKey: 'cancellation.tier1To3Days', pct: 30 },
  { labelKey: 'cancellation.tierSameDay', pct: 0 },
]

/** 手続き料を差し引いた実際の返金額 (yen)。サーバの計算と一致させること。 */
export function prepaidRefundAmount(totalAmount: number, tierPct: number): number {
  if (tierPct === 0) return 0
  const netPct = Math.max(0, tierPct - CANCELLATION_HANDLING_FEE_PCT)
  return Math.floor((totalAmount * netPct) / 100)
}


/** 手付金は binary: 7 日以上前 = 全額返金、それ未満 = 没収 (refund-policy.md §9.1 (b))。 */
export const DEPOSIT_FULL_REFUND_HOURS = 24 * 7

export function isDepositRefundable(hoursUntilReservation: number): boolean {
  return hoursUntilReservation >= DEPOSIT_FULL_REFUND_HOURS
}

/** 前払いの段階返金しきい値 (時間, 率)。サーバの PREPAID_REFUND_TIERS_HOURS_PCT と対。 */
const PREPAID_TIERS_HOURS_PCT: [number, number][] = [
  [24 * 7, 100],
  [24 * 3, 70],
  [24 * 1, 30],
  [0, 0],
]

/** 予約日時までの残り時間から実返金額を出す。サーバの計算と一致させること。 */
export function prepaidRefundAmountByHours(
  totalAmount: number,
  hoursUntilReservation: number,
): number {
  for (const [thresholdHours, pct] of PREPAID_TIERS_HOURS_PCT) {
    if (hoursUntilReservation >= thresholdHours) return prepaidRefundAmount(totalAmount, pct)
  }
  return 0
}

/**
 * #1504 「なぜその返金額なのか」を客に説明するための内訳。
 * 返金額だけ見せると「全額返金のはずが減っている」と不安になるので、
 * 段階の返金率 → 手続き料 → 実返金額 の順で根拠を出す。
 */
export interface PrepaidRefundBreakdown {
  /** 段階表の返金率 (100 / 70 / 30 / 0) */
  tierPct: number
  /** この段階に入った境界 (日)。100% 段階は上限が無いので null */
  withinDays: number | null
  /** 返金率を掛けた額 (手続き料を引く前) */
  gross: number
  /** 差し引かれる手続き料 (= gross − refund。端数処理のズレを防ぐため逆算) */
  fee: number
  /** 実際に戻る額。サーバの calc_prepaid_refund_amount と一致 */
  refund: number
}

export function prepaidRefundBreakdownByHours(
  totalAmount: number,
  hoursUntilReservation: number,
): PrepaidRefundBreakdown {
  let upperHours: number | null = null
  for (const [thresholdHours, pct] of PREPAID_TIERS_HOURS_PCT) {
    if (hoursUntilReservation >= thresholdHours) {
      const refund = prepaidRefundAmount(totalAmount, pct)
      const gross = pct === 0 ? 0 : Math.floor((totalAmount * pct) / 100)
      return {
        tierPct: pct,
        withinDays: upperHours === null ? null : upperHours / 24,
        gross,
        fee: gross - refund,
        refund,
      }
    }
    upperHours = thresholdHours
  }
  return { tierPct: 0, withinDays: 1, gross: 0, fee: 0, refund: 0 }
}

/**
 * #1374 前払い・手付金コースの最低リードタイム (日)。
 * サーバの `PREPAID_MIN_LEAD_DAYS` と対にすること。
 *
 * 承認に最大 48 時間かかるため、直前の予約は店が回答しないまま予約時刻を過ぎうる。
 * 前払いは金を預かっている分、宙に浮くと影響が大きい。
 * 店はコースごとにこれ以上を設定できる (下回る設定はサーバが切り上げる)。
 */
export const PREPAID_MIN_LEAD_DAYS = 3

/**
 * #1386 席を押さえる長さ (分)。
 *
 * 予約作成時に記録した `seatMinutes` を使う。**コースが店舗の滞在時間より長い
 * 場合がある**ため (3 時間の宴会 vs 店舗設定 2 時間)、店舗設定だけで見ると
 * 席が先に空いた扱いになる。記録の無い既存予約は店舗設定で代替する。
 */
export function seatMinutesOf(
  reservation: { seatMinutes?: number },
  storeDurationMinutes?: number,
): number {
  const v = reservation.seatMinutes
  if (typeof v === 'number' && v > 0) return v
  return storeDurationMinutes && storeDurationMinutes > 0 ? storeDurationMinutes : 120
}

/**
 * 席の終了予定時刻。
 *
 * **来店時刻ではなく予約時刻から数える。** 遅刻した分だけ後ろにずらすと次の予約と
 * 衝突するため。コース 1.5h に対し席 2h と余裕を持たせてあるので、多少の遅れは吸収できる。
 */
export function seatEndsAt(
  reservation: { date: string; time: string; seatMinutes?: number },
  storeDurationMinutes?: number,
): Date {
  const start = new Date(`${reservation.date}T${reservation.time}:00+09:00`)
  return new Date(start.getTime() + seatMinutesOf(reservation, storeDurationMinutes) * 60_000)
}

/** 席の終了まで残り何分か。過ぎていれば負。 */
export function minutesUntilSeatEnd(
  reservation: { date: string; time: string; seatMinutes?: number },
  storeDurationMinutes?: number,
): number {
  return (seatEndsAt(reservation, storeDurationMinutes).getTime() - Date.now()) / 60_000
}

/**
 * 席の終了が近い / 過ぎた予約を店員に知らせる閾値 (分)。
 *
 * 10 分前。コースの提供が終わってから席の時間が来るまでに余裕があるので、
 * 早く出しすぎると「まだ食べているのに急かす」ことになる。
 */
export const SEAT_END_ALERT_MINUTES = 10

const WEEKDAY_JA = ['日', '月', '火', '水', '木', '金', '土']

/**
 * #1386 店舗向けの予約日時表示。「8/1(土) 18:30〜20:30」。
 *
 * `{date} {time}` をそのまま並べると「2026-08-0118:30–20:30」と読めてしまい、
 * 一覧で日時を拾えなかった。年は一覧では不要な情報なので落とし、曜日を足す
 * （店主は「土曜の夜」のように曜日で思い出す）。
 */
export function formatSeatRange(
  reservation: { date: string; time: string; seatMinutes?: number },
  storeDurationMinutes?: number,
  options: { withDate?: boolean } = {},
): string {
  const start = new Date(`${reservation.date}T${reservation.time}:00+09:00`)
  const end = seatEndsAt(reservation, storeDurationMinutes)
  const hm = (d: Date) =>
    `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
  const range = `${hm(start)}〜${hm(end)}`
  if (options.withDate === false) return range
  return `${start.getMonth() + 1}/${start.getDate()}(${WEEKDAY_JA[start.getDay()]}) ${range}`
}
